import React, { useCallback, useEffect, useState } from 'react';
import Button from './ui/Button';
import { procurifyLabService } from '../services/procurifyLabService';

const formatDate = (raw) => {
  if (!raw) return '—';
  const s = String(raw);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const formatMoney = (amount, currency) => {
  if (amount == null || amount === '') return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ''}`.trim();
};

const formatQty = (raw) => {
  if (raw == null || raw === '') return '—';
  const n = Number(raw);
  if (Number.isFinite(n)) return n.toLocaleString();
  return String(raw);
};

/**
 * Modal side panel: Procurify bill detail + Approve/Deny (no iframe).
 */
const LabApprovalsProcurifyBillPanel = ({
  billId,
  billPreview,
  comment,
  onClose,
  onBillUpdated,
  onError,
}) => {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(billPreview || null);
  const [error, setError] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const resolvedId =
    billId ||
    billPreview?.uuid ||
    (billPreview?.id != null ? String(billPreview.id) : '');

  const loadDetail = useCallback(async () => {
    const id = String(resolvedId || '').trim();
    if (!id) {
      setError('Bill id is missing');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const bill = await procurifyLabService.fetchBillDetail(id);
      setDetail(bill);
    } catch (e) {
      const msg = e.message || 'Could not load bill detail';
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setLoading(false);
    }
  }, [resolvedId, onError]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleApproveDeny = async (action) => {
    const id = String(resolvedId || '').trim();
    if (!id) return;
    setActionBusy(action);
    setError(null);
    try {
      const result = await procurifyLabService.approveBill({
        billId: id,
        action,
        comment: comment || undefined,
      });
      if (result.bill) {
        setDetail(result.bill);
      } else {
        await loadDetail();
      }
      if (onBillUpdated) onBillUpdated(result);
    } catch (e) {
      const msg = e.message || `Bill ${action} failed`;
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setActionBusy(null);
    }
  };

  const handleViewPdf = async () => {
    const id = String(resolvedId || '').trim();
    if (!id) return;
    setPdfBusy(true);
    try {
      const blob = await procurifyLabService.fetchBillPdfBlob(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      const msg = e.message || 'Could not open invoice PDF';
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setPdfBusy(false);
    }
  };

  const openExternal = () => {
    procurifyLabService.openBillInProcurify(detail?.uuid || resolvedId);
  };

  const currency = detail?.currency_code || '';
  const canApprove = detail?.permissions?.can_approve !== false;
  const approveExplicitlyBlocked = detail?.permissions?.can_approve === false;
  const status = detail?.status_label || detail?.status;

  return (
    <aside
      className="lab-approvals-modal-split__docusign lab-approvals-modal-split__procurify"
      aria-label="Procurify bill"
    >
      <div className="lab-approvals-docusign-panel__toolbar">
        <span className="lab-approvals-docusign-panel__label">Procurify bill</span>
        <div className="lab-approvals-docusign-panel__actions">
          <button
            type="button"
            className="lab-approvals-docusign-panel__link"
            onClick={openExternal}
          >
            Open in Procurify
          </button>
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Close panel
          </Button>
        </div>
      </div>

      <div className="lab-approvals-procurify-bill-panel__body">
        {loading && !detail ? (
          <p className="lab-approvals-docusign-panel__note">Loading bill…</p>
        ) : null}
        {error ? (
          <p className="lab-approvals-docusign__error" role="alert">
            {error}
          </p>
        ) : null}

        {detail ? (
          <>
            <dl className="lab-approvals-procurify-summary lab-approvals-procurify-summary--panel">
              <div>
                <dt>Invoice #</dt>
                <dd>{detail.invoice_number || '—'}</dd>
              </div>
              <div>
                <dt>Vendor</dt>
                <dd>{detail.vendor_name || '—'}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{status || '—'}</dd>
              </div>
              <div>
                <dt>Due</dt>
                <dd>{formatDate(detail.due_date)}</dd>
              </div>
              <div>
                <dt>Submitted</dt>
                <dd>{formatDate(detail.submitted_date)}</dd>
              </div>
              <div>
                <dt>Invoice date</dt>
                <dd>{formatDate(detail.invoice_date)}</dd>
              </div>
              <div>
                <dt>Subtotal</dt>
                <dd>{formatMoney(detail.subtotal_cost, currency)}</dd>
              </div>
              <div>
                <dt>Total (incl. tax)</dt>
                <dd>{formatMoney(detail.total_cost_with_tax, currency)}</dd>
              </div>
              {detail.approver?.email ? (
                <div className="lab-approvals-procurify-summary__wide">
                  <dt>Current approver</dt>
                  <dd>{detail.approver.name || detail.approver.email}</dd>
                </div>
              ) : null}
            </dl>

            {Array.isArray(detail.items) && detail.items.length > 0 ? (
              <div className="lab-approvals-procurify-lines">
                <h3 className="lab-approvals-procurify-lines__title">Line items</h3>
                <div className="lab-approvals-procurify-lines__scroll">
                  <table className="lab-approvals-procurify-lines__table">
                    <thead>
                      <tr>
                        <th scope="col">Description</th>
                        <th scope="col">Qty</th>
                        <th scope="col">Unit</th>
                        <th scope="col">Total</th>
                        <th scope="col">Account</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.items.map((line, idx) => (
                        <tr key={idx}>
                          <td>{line.description || '—'}</td>
                          <td>{formatQty(line.quantity)}</td>
                          <td>{formatMoney(line.unit_cost, currency)}</td>
                          <td>{formatMoney(line.total_cost, currency)}</td>
                          <td>
                            {line.account_code || line.account_name
                              ? [line.account_code, line.account_name].filter(Boolean).join(' · ')
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {Array.isArray(detail.invoice_attachments) &&
            detail.invoice_attachments.length > 0 ? (
              <p className="lab-approvals-procurify-attachment">
                Attachment:{' '}
                <strong>{detail.invoice_attachments[0].name || 'Invoice PDF'}</strong>
              </p>
            ) : null}

            <div className="lab-approvals-procurify-bill-panel__actions">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleViewPdf}
                disabled={pdfBusy || loading}
              >
                {pdfBusy ? 'Opening PDF…' : 'View invoice PDF'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={loadDetail}
                disabled={loading || actionBusy}
              >
                Refresh
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => handleApproveDeny('approve')}
                disabled={!canApprove || loading || actionBusy}
                title={
                  canApprove
                    ? 'Approve as PROCURIFY_BILLS_ASSIGNEE_EMAIL'
                    : 'Procurify reports this bill cannot be approved via API'
                }
              >
                {actionBusy === 'approve' ? 'Approving…' : 'Approve bill'}
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => handleApproveDeny('deny')}
                disabled={!canApprove || loading || actionBusy}
              >
                {actionBusy === 'deny' ? 'Denying…' : 'Deny bill'}
              </Button>
            </div>

            {approveExplicitlyBlocked ? (
              <p className="lab-approvals-docusign-panel__note">
                Approve/Deny disabled — bill may already be processed or M2M user lacks permission.
                Use Open in Procurify if needed.
              </p>
            ) : (
              <p className="lab-approvals-docusign-panel__note">
                Approval posts as the configured Procurify assignee (server env), not your Microsoft
                login.
              </p>
            )}
          </>
        ) : null}
      </div>
    </aside>
  );
};

export default LabApprovalsProcurifyBillPanel;
